import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Calendar, Edit, Trash2, Instagram, Twitter, Facebook, Hash, CheckCircle2, Clock, FileText, Upload, Image } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// TikTok icon component (lucide doesn't have it)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

// Import Posts Component with tabs for Image and Text
const ImportPostsContent = ({ selectedArtist, importing, setImporting, setImportDialogOpen, fetchPosts, artists }) => {
  const [activeTab, setActiveTab] = useState('text'); // 'image' or 'text'
  const [textContent, setTextContent] = useState('');

  const handleImageImport = async (e) => {
    e.preventDefault();
    const fileInput = document.getElementById('import-post-image-input');
    const file = fileInput?.files[0];
    if (!file) {
      toast.error('Please select an image');
      return;
    }

    setImporting('Processing image with AI...');
    toast.info('Analyzing image...');

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API}/social-posts/import-from-image`,
          {
            image_data: event.target.result,
            artist_id: selectedArtist || null
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success(response.data.message);
        setImportDialogOpen(false);
        setImporting(null);
        fetchPosts();
      } catch (error) { // Silent fail
        toast.error(error.response?.data?.detail || 'Failed to import posts');
        setImporting(null);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleTextImport = async (e) => {
    e.preventDefault();
    
    if (!textContent.trim()) {
      toast.error('Please paste some text content');
      return;
    }

    setImporting('Processing text with AI...');
    toast.info('Analyzing text...');

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/social-posts/import-from-text`,
        {
          text_content: textContent,
          artist_id: selectedArtist || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      setImportDialogOpen(false);
      setImporting(null);
      setTextContent('');
      fetchPosts();
    } catch (error) { // Silent fail
      toast.error(error.response?.data?.detail || 'Failed to import posts from text');
      setImporting(null);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      {/* Tab Buttons */}
      <div className="flex gap-2 mb-4">
        <Button
          type="button"
          variant={activeTab === 'text' ? 'default' : 'outline'}
          onClick={() => setActiveTab('text')}
          className="flex-1"
          data-testid="import-text-tab"
        >
          <FileText className="w-4 h-4 mr-2" />
          Paste Text
        </Button>
        <Button
          type="button"
          variant={activeTab === 'image' ? 'default' : 'outline'}
          onClick={() => setActiveTab('image')}
          className="flex-1"
          data-testid="import-image-tab"
        >
          <Image className="w-4 h-4 mr-2" />
          From Image
        </Button>
      </div>

      {/* Text Import Tab */}
      {activeTab === 'text' && (
        <form onSubmit={handleTextImport} className="space-y-4">
          <div className="space-y-2">
            <Label>Paste your content</Label>
            <Textarea
              value={textContent}
              onChange={(e) => setTextContent(e.target.value)}
              placeholder={`Paste your social media content here. Examples:

Post 1 - Instagram (Dec 20)
New single dropping Friday! 🔥
#newmusic #release

---

Post 2 - TikTok (Dec 22)
Behind the scenes of the music video shoot...

---

Or paste from an email, notes, content calendar, etc.`}
              className="min-h-[200px] bg-input/50 border-border"
              data-testid="import-text-input"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            AI will detect platforms, dates, and organize posts automatically. Each distinct caption will become a separate post.
          </p>
          <Button 
            type="submit" 
            className="w-full" 
            disabled={!!importing}
            data-testid="import-text-submit"
          >
            {importing || 'Import Posts from Text'}
          </Button>
        </form>
      )}

      {/* Image Import Tab */}
      {activeTab === 'image' && (
        <form onSubmit={handleImageImport} className="space-y-4">
          <div className="space-y-2">
            <Label>Upload screenshot</Label>
            <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
              <input
                type="file"
                id="import-post-image-input"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    document.getElementById('import-post-filename').textContent = file.name;
                  }
                }}
              />
              <label htmlFor="import-post-image-input" className="cursor-pointer">
                <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Click to upload image</p>
                <p id="import-post-filename" className="text-sm text-primary mt-2"></p>
              </label>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Upload a screenshot of your content calendar, drafted posts, or any image with social media content. AI will extract all posts automatically.
          </p>
          <Button 
            type="submit" 
            className="w-full" 
            disabled={!!importing}
            data-testid="import-image-submit"
          >
            {importing || 'Import Posts from Image'}
          </Button>
        </form>
      )}
    </div>
  );
};

const Social = ({ user, selectedArtist }) => {
  const [posts, setPosts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [artists, setArtists] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(null);
  
  const [formData, setFormData] = useState({
    platform: 'Instagram',
    post_type: 'Post',
    caption: '',
    hashtags: '',
    scheduled_date: '',
    status: 'draft',
    campaign_id: '',
    artist_id: '',
    notes: '',
  });

  const PLATFORMS = [
    { name: 'Instagram', icon: Instagram, color: 'bg-pink-500', types: ['Post', 'Story', 'Reel'] },
    { name: 'TikTok', icon: TikTokIcon, color: 'bg-black', types: ['Video'] },
    { name: 'Twitter', icon: Twitter, color: 'bg-blue-400', types: ['Tweet', 'Thread'] },
    { name: 'Facebook', icon: Facebook, color: 'bg-blue-600', types: ['Post', 'Story'] },
  ];

  const STATUS_CONFIG = {
    draft: { label: 'Draft', color: 'bg-gray-500', icon: FileText },
    scheduled: { label: 'Scheduled', color: 'bg-blue-500', icon: Clock },
    posted: { label: 'Posted', color: 'bg-green-500', icon: CheckCircle2 },
  };

  const fetchPosts = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = selectedArtist ? `?artist_id=${selectedArtist}` : '';
      const response = await axios.get(`${API}/social-posts${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPosts(response.data);
    } catch (error) { // Silent fail
      toast.error('Failed to fetch social posts');
    }
  }, [selectedArtist]);

  const fetchCampaigns = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/campaigns`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCampaigns(response.data);
    } catch (error) {
      console.error('Failed to fetch campaigns:', error);
    }
  }, []);

  const fetchArtists = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/artists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArtists(response.data);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
    fetchCampaigns();
    if (user?.role === 'manager' || user?.role === 'admin') {
      fetchArtists();
    }
  }, [selectedArtist, fetchPosts, fetchCampaigns, fetchArtists, user?.role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        artist_id: formData.artist_id || selectedArtist || null,
        campaign_id: formData.campaign_id || null,
        scheduled_date: new Date(formData.scheduled_date).toISOString(),
      };

      if (editingPost) {
        await axios.put(`${API}/social-posts/${editingPost.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Post updated successfully!');
      } else {
        await axios.post(`${API}/social-posts`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Post created successfully!');
      }
      
      setOpen(false);
      setEditingPost(null);
      setFormData({
        platform: 'Instagram',
        post_type: 'Post',
        caption: '',
        hashtags: '',
        scheduled_date: '',
        status: 'draft',
        campaign_id: '',
        artist_id: '',
        notes: '',
      });
      fetchPosts();
    } catch (error) { // Silent fail
      toast.error(editingPost ? 'Failed to update post' : 'Failed to create post');
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      platform: post.platform,
      post_type: post.post_type,
      caption: post.caption,
      hashtags: post.hashtags || '',
      scheduled_date: new Date(post.scheduled_date).toISOString().slice(0, 16),
      status: post.status,
      campaign_id: post.campaign_id || '',
      artist_id: post.artist_id || '',
      notes: post.notes || '',
    });
    setOpen(true);
  };

  const deletePost = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/social-posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Post deleted!');
      fetchPosts();
    } catch (error) { // Silent fail
      toast.error('Failed to delete post');
    }
  };

  const getPlatformIcon = (platformName) => {
    const platform = PLATFORMS.find(p => p.name === platformName);
    return platform ? platform.icon : FileText;
  };

  const getPlatformColor = (platformName) => {
    const platform = PLATFORMS.find(p => p.name === platformName);
    return platform ? platform.color : 'bg-gray-500';
  };

  const getPostTypeOptions = () => {
    const platform = PLATFORMS.find(p => p.name === formData.platform);
    return platform ? platform.types : ['Post'];
  };

  // Group posts by date for calendar view
  const groupPostsByDate = () => {
    const grouped = {};
    posts.forEach(post => {
      const date = new Date(post.scheduled_date).toISOString().split('T')[0];
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(post);
    });
    return grouped;
  };

  const groupedPosts = groupPostsByDate();
  const sortedDates = Object.keys(groupedPosts).sort();

  return (
    <div className="space-y-8" data-testid="social-page">
      <div className="space-y-4">
        <div>
          <h1 className="font-manrope font-extrabold text-3xl md:text-4xl text-foreground">Social Media Calendar</h1>
          <p className="text-muted-foreground text-base mt-2">Plan and schedule your social media content</p>
        </div>
        
        <div className="flex flex-wrap gap-3">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(124,58,237,0.3)]"
                data-testid="add-post-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Post
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border/50 max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
              <DialogHeader className="flex-shrink-0">
                <DialogTitle className="font-manrope font-bold text-2xl">
                  {editingPost ? 'Edit Social Post' : 'Add Social Post'}
                </DialogTitle>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto pr-2">
                <form onSubmit={handleSubmit} className="space-y-6 pb-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="platform">Platform</Label>
                      <select
                        id="platform"
                        data-testid="platform-select"
                        value={formData.platform}
                        onChange={(e) => {
                          const newPlatform = e.target.value;
                          const platformTypes = PLATFORMS.find(p => p.name === newPlatform)?.types || ['Post'];
                          setFormData({ ...formData, platform: newPlatform, post_type: platformTypes[0] });
                        }}
                        className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                      >
                        {PLATFORMS.map((platform) => (
                          <option key={platform.name} value={platform.name}>
                            {platform.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="post_type">Post Type</Label>
                      <select
                        id="post_type"
                        data-testid="post-type-select"
                        value={formData.post_type}
                        onChange={(e) => setFormData({ ...formData, post_type: e.target.value })}
                        className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                      >
                        {getPostTypeOptions().map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="caption">Caption</Label>
                    <Textarea
                      id="caption"
                      data-testid="caption-input"
                      value={formData.caption}
                      onChange={(e) => setFormData({ ...formData, caption: e.target.value })}
                      required
                      className="bg-input/50 border-border rounded-xl min-h-32"
                      placeholder="Write your post caption..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="hashtags">Hashtags</Label>
                    <Input
                      id="hashtags"
                      data-testid="hashtags-input"
                      value={formData.hashtags}
                      onChange={(e) => setFormData({ ...formData, hashtags: e.target.value })}
                      className="bg-input/50 border-border rounded-xl"
                      placeholder="#example #hashtags"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="scheduled_date">Scheduled Date & Time</Label>
                      <Input
                        id="scheduled_date"
                        type="datetime-local"
                        data-testid="scheduled-date-input"
                        value={formData.scheduled_date}
                        onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                        required
                        className="bg-input/50 border-border rounded-xl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="status">Status</Label>
                      <select
                        id="status"
                        data-testid="status-select"
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                      >
                        <option value="draft">Draft</option>
                        <option value="scheduled">Scheduled</option>
                        <option value="posted">Posted</option>
                      </select>
                    </div>
                  </div>

                  {user?.role === 'manager' || user?.role === 'admin' && (
                    <div className="space-y-2">
                      <Label htmlFor="artist_id">Artist (Optional)</Label>
                      <select
                        id="artist_id"
                        data-testid="artist-select"
                        value={formData.artist_id || selectedArtist || ''}
                        onChange={(e) => setFormData({ ...formData, artist_id: e.target.value })}
                        className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                      >
                        <option value="">Select artist (optional)</option>
                        {artists.map((artist) => (
                          <option key={artist.id} value={artist.id}>
                            {artist.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="campaign_id">Link to Campaign (Optional)</Label>
                    <select
                      id="campaign_id"
                      data-testid="campaign-select"
                      value={formData.campaign_id}
                      onChange={(e) => setFormData({ ...formData, campaign_id: e.target.value })}
                      className="w-full px-4 py-2 bg-input/50 border border-border text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none"
                    >
                      <option value="">No campaign</option>
                      {campaigns.map((campaign) => (
                        <option key={campaign.id} value={campaign.id}>
                          {campaign.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notes (Optional)</Label>
                    <Textarea
                      id="notes"
                      data-testid="notes-input"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="bg-input/50 border-border rounded-xl min-h-24"
                      placeholder="Internal notes..."
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setOpen(false);
                        setEditingPost(null);
                        setFormData({
                          platform: 'Instagram',
                          post_type: 'Post',
                          caption: '',
                          hashtags: '',
                          scheduled_date: '',
                          status: 'draft',
                          campaign_id: '',
                          artist_id: '',
                          notes: '',
                        });
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      data-testid="submit-post-button"
                      className="flex-1 bg-primary hover:bg-primary/90"
                    >
                      {editingPost ? 'Update Post' : 'Add Post'}
                    </Button>
                  </div>
                </form>
              </div>
            </DialogContent>
          </Dialog>

          {/* Import Posts Dialog */}
          {(user?.role === 'manager' || user?.role === 'admin') && (
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  className="border-primary/50 hover:bg-primary/10"
                  data-testid="import-posts-button"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Import Posts
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border/50 max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader className="flex-shrink-0">
                  <DialogTitle className="font-manrope font-bold text-2xl">
                    Import Social Posts
                  </DialogTitle>
                  <p className="text-sm text-muted-foreground">
                    Use AI to import posts from text or images
                  </p>
                </DialogHeader>
                <ImportPostsContent
                  selectedArtist={selectedArtist}
                  importing={importing}
                  setImporting={setImporting}
                  setImportDialogOpen={setImportDialogOpen}
                  fetchPosts={fetchPosts}
                  artists={artists}
                />
              </DialogContent>
            </Dialog>
          )}

          <Button
            variant={viewMode === 'calendar' ? 'default' : 'outline'}
            onClick={() => setViewMode('calendar')}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Calendar View
          </Button>
          <Button
            variant={viewMode === 'list' ? 'default' : 'outline'}
            onClick={() => setViewMode('list')}
          >
            List View
          </Button>
        </div>
      </div>

      {posts.length === 0 ? (
        <Card className="bg-card border-border/50 p-12 text-center">
          <p className="text-muted-foreground">No social posts scheduled yet. Add your first post!</p>
        </Card>
      ) : viewMode === 'calendar' ? (
        <div className="space-y-6">
          {sortedDates.map((date) => (
            <div key={date}>
              <h2 className="font-manrope font-bold text-xl text-foreground mb-4">
                {new Date(date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </h2>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {groupedPosts[date].map((post) => {
                  const PlatformIcon = getPlatformIcon(post.platform);
                  const StatusIcon = STATUS_CONFIG[post.status].icon;
                  
                  return (
                    <Card
                      key={post.id}
                      className="bg-card border-border/50 p-6 hover:border-primary/50 transition-colors"
                      data-testid={`post-item-${post.id}`}
                    >
                      <div className="space-y-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl ${getPlatformColor(post.platform)} flex items-center justify-center`}>
                              <PlatformIcon className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">{post.platform}</h3>
                              <p className="text-xs text-muted-foreground">{post.post_type}</p>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(post)}
                            className="min-w-[60px]"
                          >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                          </Button>
                        </div>

                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${STATUS_CONFIG[post.status].color}`}>
                              <StatusIcon className="w-3 h-3 inline mr-1" />
                              {STATUS_CONFIG[post.status].label}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(post.scheduled_date).toLocaleTimeString('en-US', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>

                          <p className="text-sm text-foreground line-clamp-3">{post.caption}</p>
                          
                          {post.hashtags && (
                            <div className="flex items-center gap-1 text-xs text-primary">
                              <Hash className="w-3 h-3" />
                              <p className="line-clamp-1">{post.hashtags}</p>
                            </div>
                          )}
                        </div>

                        <div className="pt-4 border-t border-border/30">
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => deletePost(post.id)}
                            className="w-full"
                            data-testid={`delete-post-${post.id}`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          {posts.sort((a, b) => new Date(a.scheduled_date) - new Date(b.scheduled_date)).map((post) => {
            const PlatformIcon = getPlatformIcon(post.platform);
            const StatusIcon = STATUS_CONFIG[post.status].icon;
            
            return (
              <Card
                key={post.id}
                className="bg-card border-border/50 p-6 hover:border-primary/50 transition-colors"
                data-testid={`post-item-${post.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`w-12 h-12 rounded-xl ${getPlatformColor(post.platform)} flex items-center justify-center flex-shrink-0`}>
                      <PlatformIcon className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        <h3 className="font-semibold text-foreground">{post.platform} • {post.post_type}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium text-white ${STATUS_CONFIG[post.status].color}`}>
                          <StatusIcon className="w-3 h-3 inline mr-1" />
                          {STATUS_CONFIG[post.status].label}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {new Date(post.scheduled_date).toLocaleDateString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                      <p className="text-sm text-foreground">{post.caption}</p>
                      {post.hashtags && (
                        <div className="flex items-center gap-1 text-xs text-primary">
                          <Hash className="w-3 h-3" />
                          <p>{post.hashtags}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(post)}
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deletePost(post.id)}
                      data-testid={`delete-post-${post.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Social;
